/**
 * SDR Stock Manager - Sub-Box Contents Utilities
 * @module utils/box-contents
 */

/**
 * Normalizes any box contents data representation (JSON string, raw array, or objects)
 * into a clean array of BoxContentItem objects.
 * @param {string|Array<any>|null|undefined} value
 * @returns {import('../types/index.js').BoxContentItem[]}
 */
export function normalizeBoxContents(value) {
  let raw = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (err) {
      raw = [];
    }
  }
  if (!Array.isArray(raw)) return [];

  return raw
    .map((box, index) => ({
      label: String((box && box.label) || `กล่อง ${index + 1}`).trim() || `กล่อง ${index + 1}`,
      quantity: Number(box && box.quantity) || 0,
      unit: String((box && box.unit) || '').trim()
    }))
    .filter(box => box.quantity > 0 || box.unit || (box.label && box.label !== ''));
}

/**
 * Summarizes total counts grouped by unit across an array of boxes.
 * @param {string|Array<any>} value
 * @returns {{ boxes: import('../types/index.js').BoxContentItem[], totals: Record<string, number> }}
 */
export function summarizeBoxContents(value) {
  const boxes = normalizeBoxContents(value);
  const totals = {};

  boxes.forEach(box => {
    const unit = box.unit || 'หน่วย';
    totals[unit] = Number(((totals[unit] || 0) + Number(box.quantity || 0)).toFixed(5));
  });

  return { boxes, totals };
}

/**
 * Formats a user-facing summary string for sub-boxes.
 * E.g. "รวม 10 ชิ้น, 5 หลอด จาก 2 กล่อง"
 * @param {string|Array<any>} value
 * @returns {string}
 */
export function formatBoxContentsSummary(value) {
  const { boxes, totals } = summarizeBoxContents(value);
  if (boxes.length === 0) return '';

  const totalText = Object.entries(totals)
    .map(([unit, qty]) => `${qty} ${unit}`)
    .join(', ');

  return `รวม ${totalText} จาก ${boxes.length} กล่อง`;
}

/**
 * Formats a detailed comma-separated list of individual boxes.
 * E.g. "กล่อง 1: 5 ชิ้น, กล่อง 2: 5 ชิ้น"
 * @param {string|Array<any>} value
 * @returns {string}
 */
export function formatBoxContentsDetail(value) {
  const boxes = normalizeBoxContents(value);
  return boxes.map(box => `${box.label}: ${box.quantity} ${box.unit || ''}`.trim()).join(', ');
}

/**
 * Canonical JSON signature for comparing box equality.
 * @param {import('../types/index.js').BoxContentItem} box
 * @returns {string}
 */
export function boxContentSignature(box) {
  const normalized = {
    label: String((box && box.label) || '').trim(),
    quantity: Number(box && box.quantity) || 0,
    unit: String((box && box.unit) || '').trim()
  };
  return JSON.stringify(normalized);
}

/**
 * Takes `count` boxes from the beginning of a box array or InventoryItem.
 * @param {import('../types/index.js').InventoryItem|Array<import('../types/index.js').BoxContentItem>} itemOrBoxes
 * @param {number} count
 * @returns {import('../types/index.js').BoxContentItem[]} Taken boxes
 */
export function takeBoxContents(itemOrBoxes, count) {
  const isItem = itemOrBoxes && typeof itemOrBoxes === 'object' && 'boxContents' in itemOrBoxes;
  const boxes = normalizeBoxContents(isItem ? itemOrBoxes.boxContents : itemOrBoxes);
  const takeCount = Math.max(0, Math.floor(Number(count) || 0));

  if (boxes.length === 0 || takeCount <= 0) return [];

  const taken = boxes.slice(0, takeCount);
  const remaining = boxes.slice(takeCount);

  if (isItem) {
    itemOrBoxes.boxContents = remaining;
    itemOrBoxes.quantity = remaining.length;
  }

  return taken;
}

/**
 * Takes boxes from specific 0-based indexes.
 * @param {import('../types/index.js').InventoryItem|Array<import('../types/index.js').BoxContentItem>} itemOrBoxes
 * @param {number[]} selectedIndexes
 * @returns {import('../types/index.js').BoxContentItem[]} Taken boxes
 */
export function takeSelectedBoxContents(itemOrBoxes, selectedIndexes) {
  const isItem = itemOrBoxes && typeof itemOrBoxes === 'object' && 'boxContents' in itemOrBoxes;
  const boxes = normalizeBoxContents(isItem ? itemOrBoxes.boxContents : itemOrBoxes);

  const indexes = Array.from(new Set((selectedIndexes || [])
    .map(index => Number(index))
    .filter(index => Number.isInteger(index) && index >= 0 && index < boxes.length)
  ));

  if (boxes.length === 0 || indexes.length === 0) return [];

  const selectedSet = new Set(indexes);
  const taken = indexes.map(index => boxes[index]);
  const remaining = boxes.filter((_, index) => !selectedSet.has(index));

  if (isItem) {
    itemOrBoxes.boxContents = remaining;
    itemOrBoxes.quantity = remaining.length;
  }

  return taken;
}

/**
 * Removes specified box occurrences from a box list or InventoryItem.
 * @param {import('../types/index.js').InventoryItem|Array<import('../types/index.js').BoxContentItem>} itemOrBoxes
 * @param {Array<import('../types/index.js').BoxContentItem>} boxesToRemove
 * @returns {number} Count of boxes removed
 */
export function removeBoxContents(itemOrBoxes, boxesToRemove) {
  const isItem = itemOrBoxes && typeof itemOrBoxes === 'object' && 'boxContents' in itemOrBoxes;
  const currentBoxes = normalizeBoxContents(isItem ? itemOrBoxes.boxContents : itemOrBoxes);

  const removeCounts = {};
  normalizeBoxContents(boxesToRemove).forEach(box => {
    const key = boxContentSignature(box);
    removeCounts[key] = (removeCounts[key] || 0) + 1;
  });

  let removedCount = 0;
  const remaining = currentBoxes.filter(box => {
    const key = boxContentSignature(box);
    if (removeCounts[key] > 0) {
      removeCounts[key] -= 1;
      removedCount += 1;
      return false;
    }
    return true;
  });

  if (isItem) {
    itemOrBoxes.boxContents = remaining;
    itemOrBoxes.quantity = remaining.length;
  }

  return removedCount;
}

/**
 * Validates return box remaining counts.
 * @param {Array<import('../types/index.js').BoxContentItem>} borrowedBoxes
 * @param {Array<{ index: number, remaining: number|string }>} returnInputs
 * @returns {string} Error message or empty string if valid
 */
export function validateReturnBoxContents(borrowedBoxes, returnInputs) {
  const boxes = normalizeBoxContents(borrowedBoxes);
  if (!Array.isArray(returnInputs)) return '';

  for (const input of returnInputs) {
    if (input.remaining === undefined || input.remaining === null || String(input.remaining).trim() === '') {
      continue;
    }
    const idx = Number(input.index);
    const remaining = Number(input.remaining);

    if (!boxes[idx] || !Number.isFinite(remaining) || remaining < 0) {
      return 'กรุณากรอกจำนวนชิ้นที่เหลือให้เป็นตัวเลขมากกว่าหรือเท่ากับ 0';
    }
  }

  return '';
}
